import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * useToastSubscriptions
 * Subscribes to audit_logs, faction_messages, faction_wars, notifications
 * and fires toast callbacks for each relevant event.
 */
export function useToastSubscriptions({
  userId,
  factionId,
  onAuditLog,
  onFactionMessage,
  onFactionWar,
  onNotification,
  enabled = true,
}) {
  const channelsRef = useRef([]);
  const mountedRef = useRef(true);

  const cleanup = useCallback(() => {
    channelsRef?.current?.forEach(ch => {
      try { supabase?.removeChannel(ch); } catch (e) {}
    });
    channelsRef.current = [];
  }, []);

  const subscribe = useCallback(() => {
    if (!userId || !enabled || !mountedRef?.current) return;
    cleanup();

    // 1. audit_logs INSERT for user actions
    const auditChannel = supabase
      ?.channel(`toast-audit-${userId}`)
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'audit_logs',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (!mountedRef?.current) return;
        onAuditLog?.(payload?.new);
      })
      ?.subscribe();

    // 2. faction_messages INSERT (pinned messages from leader)
    const factionMsgChannel = factionId
      ? supabase
          ?.channel(`toast-faction-msg-${factionId}`)
          ?.on('postgres_changes', {
            event: 'INSERT',
            schema: 'public',
            table: 'faction_messages',
            filter: `faction_id=eq.${factionId}`,
          }, (payload) => {
            if (!mountedRef?.current) return;
            if (payload?.new?.is_pinned || payload?.new?.message_type === 'announcement') {
              onFactionMessage?.(payload?.new);
            }
          })
          ?.subscribe()
      : null;

    // 3. faction_wars INSERT/UPDATE — war declarations
    const warChannel = factionId
      ? supabase
          ?.channel(`toast-faction-war-${factionId}`)
          ?.on('postgres_changes', {
            event: 'INSERT',
            schema: 'public',
            table: 'faction_wars',
            filter: `defender_faction_id=eq.${factionId}`,
          }, (payload) => {
            if (!mountedRef?.current) return;
            onFactionWar?.(payload?.new);
          })
          ?.subscribe()
      : null;

    // 4. notifications INSERT for user
    const notifChannel = supabase
      ?.channel(`toast-notifications-${userId}`)
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (!mountedRef?.current) return;
        onNotification?.(payload?.new);
      })
      ?.subscribe();

    channelsRef.current = [auditChannel, factionMsgChannel, warChannel, notifChannel]?.filter(Boolean);
  }, [userId, factionId, enabled, onAuditLog, onFactionMessage, onFactionWar, onNotification, cleanup]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled && userId) subscribe();
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [subscribe, enabled, userId, factionId]);
}
